import { PDFDocument, StandardFonts, rgb, PageSizes } from "pdf-lib";
import { IconDownload } from "@tabler/icons-react";

interface NdaTemplateProps {
  company1Name: string;
  company1Address: string;
  company2Name: string;
  company2Address: string;
  matchId?: string | null;
  company2Data?: any;
  company1ShortName?: string;
  company2ShortName?: string;
  company1IncorporationType?: string;
  company2IncorporationType?: string;
  company1IncorporationLaw?: string;
  company2IncorporationLaw?: string;
  governingLaw?: string;
  platformName?: string;
  platformUrl?: string;
  currentUserCompany?: "company1" | "company2";
  showAllDetails?: boolean;
  company1SignedInDb?: boolean;
  company2SignedInDb?: boolean;
}

// ─── NDA Content ──────────────────────────────────────────────────────────────

const PARTIES_INTRO = `This Mutual Non-Disclosure Agreement (the "Agreement") is made and effective as of the last date of acceptance by a Party (the "Effective Date")`;

const getWhereas = (platformName: string, platformUrl: string) =>
  `WHEREAS, each party ("Disclosing Party") may disclose information to the other ("Recipient") on the Platform, information that relates to the Disclosing Party's business strategy, finances or other business information, including quotes, concepts, sales and/or marketing proposals, strategies, designs, data, trade secrets and know-how, research, product plans, products, customer technical requirements, software, programming techniques, algorithms, services, suppliers, customers, employees, markets, forecasts, intellectual property, technology, engineering, apparatus, techniques, hardware configuration information, and any other confidential information ("Confidential Information") solely on the platform operated by ${platformName} at ${platformUrl} ("Platform"). The Parties expressly agree that this Agreement governs only such Confidential Information as is disclosed by either Party solely through the Platform. For the avoidance of doubt, any information exchanged by any other means, including but not limited to oral communications, written correspondence, electronic communications, meetings, discussions, or through any other platform or medium, shall be excluded from the scope of this Agreement and shall not be treated as Confidential Information hereunder.`;

const CLAUSES = [
  {
    num: "1",
    title: "Confidentiality Obligations",
    body: `Recipient shall not disclose the Disclosing Party's Confidential Information to a third party or use the Disclosing Party's Confidential Information except as authorized by the Disclosing Party and contemplated by the nature of the business relationship between the parties. Recipient shall protect the confidentiality of the Disclosing Party's Confidential Information in the same manner that it protects the confidentiality of its own confidential information of like kind and shall disclose such information to (i) its employees and independent contractors who have a need to know; and (ii) any other party with the Disclosing Party's prior written consent. Before disclosure to any of the above parties, the Recipient must have an appropriate written agreement with such party sufficient to require that the party treats such Confidential Information in accordance with this Agreement.`,
  },
  {
    num: "2",
    title: "Exceptions",
    body: `This Agreement shall not restrict Recipient's use of information (including, but not limited to, ideas, concepts, know-how, techniques and methodologies): (a) which is or becomes publicly available through no breach of this Agreement; (b) independently developed by Recipient; (c) previously known to Recipient without obligation of confidence; or (d) acquired by Recipient from a third party which is not, to Recipient's knowledge, subject to an agreement of confidentiality with respect to such information.`,
  },
  {
    num: "3",
    title: "Legal Compulsion",
    body: `In the event Recipient receives a subpoena or other administrative or judicial process requesting Confidential Information, Recipient shall notify the Disclosing Party of such receipt to allow the Disclosing Party a reasonable opportunity to obtain a protective order. The Recipient may thereafter comply with such subpoena or process to the extent required by law.`,
  },
  {
    num: "4",
    title: "Competitive Activities",
    body: `This Agreement shall not restrict Recipient's right to develop, use, or market products or services similar to, or competitive with, products or services of the Disclosing Party, so long as such development, use, or marketing does not contain any Confidential Information.`,
  },
  {
    num: "5",
    title: "No License",
    body: `Neither this Agreement, nor any disclosure of Confidential Information hereunder grants the Recipient any right or license under any trademark, trade secret, copyright or patent now or hereafter owned or controlled by the Disclosing Party.`,
  },
  {
    num: "6",
    title: "Disclaimer of Warranties",
    body: `All CONFIDENTIAL INFORMATION IS PROVIDED "AS IS." DISCLOSING PARTY MAKES NO WARRANTIES, EXPRESS, IMPLIED OR OTHERWISE, REGARDING ITS CONFIDENTIAL INFORMATION.`,
  },
  {
    num: "7",
    title: "Assignment",
    body: `Neither party shall assign this Agreement without the other party's consent. Notwithstanding the foregoing, however, a party shall have the right to assign this Agreement to its affiliate, subsidiary or an entity that purchases all or substantially of its assets as a result of a merger, sale of equity interest or otherwise, provided that the entity agrees in writing to be bound by the terms of this Agreement, and provided that such assignment shall not release the party from its obligations hereunder.`,
  },
  {
    num: "8",
    title: "Injunctive Relief",
    body: `Each party acknowledges that monetary damages may not be a sufficient remedy for unauthorized use or disclosure of Confidential Information of the other party, and that in the event of a breach or threatened breach of the Agreement, the other party shall be entitled, without waiving any other rights or remedies, to seek injunctive or equitable relief as may be deemed proper by a court of competent jurisdiction.`,
  },
  {
    num: "9",
    title: "Term and Termination",
    body: `Either party may terminate this Agreement by providing thirty (30) days advance written notice to the other. Documents or other materials containing Confidential Information shall be promptly returned or destroyed upon the Disclosing Party's request. The restrictions imposed by this Agreement shall last for a period of three (3) years following termination of this Agreement.`,
  },
  {
    num: "10",
    title: "Governing Law",
    body: `This Agreement shall be governed by the laws of __________. This Agreement is the complete and exclusive agreement regarding disclosure of the Confidential Information.`,
  },
];

// ─── Helper: Mask text ────────────────────────────────────────────────────────
const HIDDEN_PLACEHOLDER = "[Hidden until both parties accept]";

// ─── PDF generation ───────────────────────────────────────────────────────────

async function generatePdf({
  company1Name,
  company1Address,
  company2Name,
  company2Address,
  company1ShortName,
  company2ShortName,
  company1IncorporationType,
  company2IncorporationType,
  company1IncorporationLaw,
  company2IncorporationLaw,
  governingLaw,
  platformName,
  platformUrl,
  currentUserCompany,
  showAllDetails,
  company1SignedInDb,
  company2SignedInDb,
}: {
  company1Name: string;
  company1Address: string;
  company2Name: string;
  company2Address: string;
  company1ShortName: string;
  company2ShortName: string;
  company1IncorporationType: string;
  company2IncorporationType: string;
  company1IncorporationLaw: string;
  company2IncorporationLaw: string;
  governingLaw: string;
  platformName: string;
  platformUrl: string;
  currentUserCompany: "company1" | "company2";
  showAllDetails: boolean;
  company1SignedInDb: boolean;
  company2SignedInDb: boolean;
}) {
  const bothSignedInDb = company1SignedInDb && company2SignedInDb;

  const showCompany1Details = showAllDetails || bothSignedInDb || currentUserCompany === "company1";
  const showCompany2Details = showAllDetails || bothSignedInDb || currentUserCompany === "company2";

  const displayCompany1Name = showCompany1Details ? company1Name : HIDDEN_PLACEHOLDER;
  const displayCompany1Address = showCompany1Details ? company1Address : HIDDEN_PLACEHOLDER;
  const displayCompany1ShortName = showCompany1Details ? company1ShortName : "Party A";
  const displayCompany1IncorporationType = showCompany1Details ? company1IncorporationType : "___";
  const displayCompany1IncorporationLaw = showCompany1Details ? company1IncorporationLaw : "___";

  const displayCompany2Name = showCompany2Details ? company2Name : HIDDEN_PLACEHOLDER;
  const displayCompany2Address = showCompany2Details ? company2Address : HIDDEN_PLACEHOLDER;
  const displayCompany2ShortName = showCompany2Details ? company2ShortName : "Party B";
  const displayCompany2IncorporationType = showCompany2Details ? company2IncorporationType : "___";
  const displayCompany2IncorporationLaw = showCompany2Details ? company2IncorporationLaw : "___";

  const pdfDoc = await PDFDocument.create();
  const timesBold = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);
  const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const helveticaOblique = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);

  const pageWidth = PageSizes.A4[0];
  const pageHeight = PageSizes.A4[1];
  const marginX = 56;
  const contentWidth = pageWidth - marginX * 2;

  const dark = rgb(0.1, 0.1, 0.1);
  const gray = rgb(0.47, 0.47, 0.47);
  const light = rgb(0.75, 0.75, 0.75);
  const green = rgb(0.07, 0.53, 0.35);

  function wrapText(text: string, font: typeof helvetica, size: number, maxWidth: number): string[] {
    const words = text.split(" ");
    const lines: string[] = [];
    let current = "";
    for (const word of words) {
      const test = current ? `${current} ${word}` : word;
      if (font.widthOfTextAtSize(test, size) <= maxWidth) {
        current = test;
      } else {
        if (current) lines.push(current);
        current = word;
      }
    }
    if (current) lines.push(current);
    return lines;
  }

  function drawParagraph(
    page: ReturnType<typeof pdfDoc.addPage>,
    text: string,
    font: typeof helvetica,
    size: number,
    x: number,
    startY: number,
    maxWidth: number,
    lineHeight: number,
    color = dark,
    justify = false
  ): number {
    let y = startY;
    const lines = wrapText(text, font, size, maxWidth);
    lines.forEach((line, idx) => {
      const isLast = idx === lines.length - 1;
      const words = line.split(" ");
      if (justify && !isLast && words.length > 1) {
        const totalWordWidth = words.reduce((sum, w) => sum + font.widthOfTextAtSize(w, size), 0);
        const gap = (maxWidth - totalWordWidth) / (words.length - 1);
        let curX = x;
        for (const word of words) {
          page.drawText(word, { x: curX, y, font, size, color });
          curX += font.widthOfTextAtSize(word, size) + gap;
        }
      } else {
        page.drawText(line, { x, y, font, size, color });
      }
      y -= lineHeight;
    });
    return y;
  }

  let page = pdfDoc.addPage([pageWidth, pageHeight]);
  let y = pageHeight - 52;

  // ── Title ─────────────────────────────────────────────────────────────────
  const titleText = "MUTUAL NON-DISCLOSURE AGREEMENT";
  const titleWidth = timesBold.widthOfTextAtSize(titleText, 14);
  page.drawText(titleText, { x: (pageWidth - titleWidth) / 2, y, font: timesBold, size: 14, color: dark });
  y -= 20;

  page.drawLine({ start: { x: marginX, y }, end: { x: pageWidth - marginX, y }, thickness: 1.2, color: dark });
  y -= 16;

  // ── Preamble ──────────────────────────────────────────────────────────────
  y = drawParagraph(page, PARTIES_INTRO, helvetica, 9.5, marginX, y, contentWidth, 14, dark, true);
  y -= 10;

  // ── BETWEEN block ─────────────────────────────────────────────────────────
  page.drawText("BETWEEN", { x: marginX, y, font: helveticaBold, size: 9.5, color: dark });
  y -= 14;

  const drawMixedText = (
    page: ReturnType<typeof pdfDoc.addPage>,
    segments: { text: string; bold?: boolean }[],
    startX: number,
    startY: number,
    maxWidth: number,
    lineHeight: number
  ): number => {
    // Tokenize all segments into individual words with font info
    const tokens: { word: string; bold?: boolean }[] = [];
    for (const seg of segments) {
      const words = seg.text.split(/(\s+)/).filter((w) => w.trim().length > 0);
      for (const word of words) {
        tokens.push({ word, bold: seg.bold });
      }
    }

    // Build lines
    const lines: { word: string; bold?: boolean }[][] = [];
    let currentLineTokens: { word: string; bold?: boolean }[] = [];
    let currentWidth = 0;

    for (const token of tokens) {
      const f = token.bold ? helveticaBold : helvetica;
      const wordWidth = f.widthOfTextAtSize(token.word, 9.5);
      const spaceWidth = currentLineTokens.length > 0 ? helvetica.widthOfTextAtSize(" ", 9.5) : 0;

      if (currentLineTokens.length > 0 && currentWidth + spaceWidth + wordWidth > maxWidth) {
        lines.push(currentLineTokens);
        currentLineTokens = [token];
        currentWidth = wordWidth;
      } else {
        currentLineTokens.push(token);
        currentWidth += spaceWidth + wordWidth;
      }
    }
    if (currentLineTokens.length > 0) lines.push(currentLineTokens);

    let y = startY;
    lines.forEach((lineTokens, idx) => {
      const isLast = idx === lines.length - 1;

      if (!isLast && lineTokens.length > 1) {
        // Justified: distribute extra space between words
        const totalWordWidth = lineTokens.reduce((sum, t) => {
          const f = t.bold ? helveticaBold : helvetica;
          return sum + f.widthOfTextAtSize(t.word, 9.5);
        }, 0);
        const gap = (maxWidth - totalWordWidth) / (lineTokens.length - 1);
        let x = startX;
        for (const token of lineTokens) {
          const f = token.bold ? helveticaBold : helvetica;
          page.drawText(token.word, { x, y, font: f, size: 9.5, color: dark });
          x += f.widthOfTextAtSize(token.word, 9.5) + gap;
        }
      } else {
        // Last line or single word: left-align
        let x = startX;
        for (const token of lineTokens) {
          const f = token.bold ? helveticaBold : helvetica;
          page.drawText(token.word, { x, y, font: f, size: 9.5, color: dark });
          x += f.widthOfTextAtSize(token.word, 9.5) + helvetica.widthOfTextAtSize(" ", 9.5);
        }
      }
      y -= lineHeight;
    });
    return y;
  };

  const party1Segments = [
    { text: displayCompany1Name, bold: true },
    { text: `, a ${displayCompany1IncorporationType}, incorporated under the laws of ` },
    { text: displayCompany1IncorporationLaw, bold: true },
    {
      text: `, having its registered office at ${displayCompany1Address} (hereinafter referred to as "${displayCompany1ShortName}", which expression shall, unless repugnant to the context or meaning thereof, include its successors and permitted assigns);`,
    },
  ];
  y = drawMixedText(page, party1Segments, marginX, y, contentWidth, 14);
  y -= 10;

  page.drawText("AND", { x: marginX, y, font: helveticaBold, size: 9.5, color: dark });
  y -= 14;

  const party2Segments = [
    { text: displayCompany2Name, bold: true },
    { text: `, a ${displayCompany2IncorporationType}, incorporated under the laws of ` },
    { text: displayCompany2IncorporationLaw, bold: true },
    {
      text: `, having its registered office at ${displayCompany2Address} (hereinafter referred to as "${displayCompany2ShortName}", which expression shall, unless repugnant to the context or meaning thereof, include its successors and permitted assigns);`,
    },
  ];
  y = drawMixedText(page, party2Segments, marginX, y, contentWidth, 14);
  y -= 14;

  // ── WHEREAS ───────────────────────────────────────────────────────────────
  if (y < 120) {
    page = pdfDoc.addPage([pageWidth, pageHeight]);
    y = pageHeight - 52;
  }
  page.drawText("WHEREAS,", { x: marginX, y, font: helveticaBold, size: 9.5, color: dark });
  y -= 14;
  const whereasBody = getWhereas(platformName, platformUrl).replace(/^WHEREAS, /, "");
  y = drawParagraph(page, whereasBody, helvetica, 9.5, marginX, y, contentWidth, 14, dark, true);
  y -= 14;

  // ── Clauses ───────────────────────────────────────────────────────────────
  for (const clause of CLAUSES) {
    if (y < 100) {
      page = pdfDoc.addPage([pageWidth, pageHeight]);
      y = pageHeight - 52;
    }

    page.drawText(`${clause.num}. ${clause.title}`, { x: marginX, y, font: helveticaBold, size: 9.5, color: dark });
    y -= 14;

    const bodyText =
      clause.num === "10"
        ? `This Agreement shall be governed by the laws of ${governingLaw}. This Agreement is the complete and exclusive agreement regarding disclosure of the Confidential Information.`
        : clause.body;

    y = drawParagraph(page, bodyText, helvetica, 9.5, marginX, y, contentWidth, 14, dark, true);
    y -= 12;
  }

  // ── Divider ───────────────────────────────────────────────────────────────
  if (y < 200) {
    page = pdfDoc.addPage([pageWidth, pageHeight]);
    y = pageHeight - 52;
  }
  y -= 4;
  page.drawLine({ start: { x: marginX, y }, end: { x: pageWidth - marginX, y }, thickness: 0.5, color: light });
  y -= 14;

  // ── Witness clause ────────────────────────────────────────────────────────
  const witness = "IN WITNESS WHEREOF, the Parties have executed this Agreement as of the Effective Date.";
  y = drawParagraph(page, witness, helveticaOblique, 9, marginX, y, contentWidth, 14, gray, true);
  y -= 18;

  // ── Acceptance blocks ─────────────────────────────────────────────────────
  if (y < 180) {
    page = pdfDoc.addPage([pageWidth, pageHeight]);
    y = pageHeight - 52;
  }

  const colWidth = (contentWidth - 24) / 2;
  const col1X = marginX;
  const col2X = marginX + colWidth + 24;
  const sigStartY = y;

  page.drawText(displayCompany1Name || "Company 1", {
    x: col1X,
    y: sigStartY,
    font: helveticaBold,
    size: 9.5,
    color: dark,
  });
  page.drawText(displayCompany2Name || "Company 2", {
    x: col2X,
    y: sigStartY,
    font: helveticaBold,
    size: 9.5,
    color: dark,
  });
  y = sigStartY - 16;

  page.drawText("REGISTERED ADDRESS", { x: col1X, y, font: helveticaBold, size: 8, color: gray });
  page.drawText("REGISTERED ADDRESS", { x: col2X, y, font: helveticaBold, size: 8, color: gray });
  y -= 13;

  const addr1Lines = wrapText(displayCompany1Address || "—", helvetica, 9.5, colWidth);
  const addr2Lines = wrapText(displayCompany2Address || "—", helvetica, 9.5, colWidth);
  const maxAddrLines = Math.max(addr1Lines.length, addr2Lines.length);
  for (let i = 0; i < maxAddrLines; i++) {
    if (addr1Lines[i]) page.drawText(addr1Lines[i], { x: col1X, y, font: helvetica, size: 9.5, color: dark });
    if (addr2Lines[i]) page.drawText(addr2Lines[i], { x: col2X, y, font: helvetica, size: 9.5, color: dark });
    y -= 13;
  }
  y -= 8;

  page.drawText("ACCEPTANCE", { x: col1X, y, font: helveticaBold, size: 8, color: gray });
  page.drawText("ACCEPTANCE", { x: col2X, y, font: helveticaBold, size: 8, color: gray });
  y -= 10;

  const boxH = 40;
  const boxY = y - boxH;

  page.drawRectangle({
    x: col1X,
    y: boxY,
    width: colWidth,
    height: boxH,
    borderColor: light,
    borderWidth: 0.75,
    color: rgb(0.98, 0.98, 0.98),
  });
  page.drawRectangle({
    x: col2X,
    y: boxY,
    width: colWidth,
    height: boxH,
    borderColor: light,
    borderWidth: 0.75,
    color: rgb(0.98, 0.98, 0.98),
  });

  const sig1Text = company1SignedInDb && showCompany1Details ? "Accepted" : "Pending acceptance";
  const sig2Text = company2SignedInDb && showCompany2Details ? "Accepted" : "Pending acceptance";
  const sig1Color = company1SignedInDb && showCompany1Details ? green : gray;
  const sig2Color = company2SignedInDb && showCompany2Details ? green : gray;

  page.drawText(sig1Text, { x: col1X + 8, y: boxY + boxH / 2 - 5, font: helvetica, size: 9, color: sig1Color });
  page.drawText(sig2Text, { x: col2X + 8, y: boxY + boxH / 2 - 5, font: helvetica, size: 9, color: sig2Color });

  // ── Footer ────────────────────────────────────────────────────────────────
  const footerY = 28;
  page.drawLine({
    start: { x: marginX, y: footerY + 12 },
    end: { x: pageWidth - marginX, y: footerY + 12 },
    thickness: 0.5,
    color: light,
  });
  const copyright = `© ${new Date().getFullYear()} GTsetu. All rights reserved. This document is confidential.`;
  page.drawText(copyright, { x: marginX, y: footerY, font: helvetica, size: 8, color: light });
  const pageLabel = "Page 1 of 1";
  const pageLabelWidth = helvetica.widthOfTextAtSize(pageLabel, 8);
  page.drawText(pageLabel, {
    x: pageWidth - marginX - pageLabelWidth,
    y: footerY,
    font: helvetica,
    size: 8,
    color: light,
  });

  return pdfDoc.save();
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function NdaTemplate({
  company1Name,
  company1Address,
  company2Name,
  company2Address,
  company1ShortName = "Party 1",
  company2ShortName = "Party 2",
  company1IncorporationType = "company/partnership",
  company2IncorporationType = "company/partnership",
  company1IncorporationLaw = "___",
  company2IncorporationLaw = "___",
  governingLaw = "__________",
  platformName = "GTsetu",
  platformUrl = "https://gtsetu.com",
  currentUserCompany = "company1",
  showAllDetails = false,
  company1SignedInDb = false,
  company2SignedInDb = false,
}: NdaTemplateProps) {
  const bothSignedInDb = company1SignedInDb && company2SignedInDb;

  const showCompany1Details = showAllDetails || bothSignedInDb || currentUserCompany === "company1";
  const showCompany2Details = showAllDetails || bothSignedInDb || currentUserCompany === "company2";

  const displayCompany1Name = showCompany1Details ? company1Name : HIDDEN_PLACEHOLDER;
  const displayCompany1Address = showCompany1Details ? company1Address : HIDDEN_PLACEHOLDER;
  const displayCompany1ShortName = showCompany1Details ? company1ShortName : "Party A";
  const displayCompany1IncorporationType = showCompany1Details ? company1IncorporationType : "___";
  const displayCompany1IncorporationLaw = showCompany1Details ? company1IncorporationLaw : "___";

  const displayCompany2Name = showCompany2Details ? company2Name : HIDDEN_PLACEHOLDER;
  const displayCompany2Address = showCompany2Details ? company2Address : HIDDEN_PLACEHOLDER;
  const displayCompany2ShortName = showCompany2Details ? company2ShortName : "Party B";
  const displayCompany2IncorporationType = showCompany2Details ? company2IncorporationType : "___";
  const displayCompany2IncorporationLaw = showCompany2Details ? company2IncorporationLaw : "___";

  const handleDownload = async () => {
    try {
      const pdfBytes = await generatePdf({
        company1Name,
        company1Address,
        company2Name,
        company2Address,
        company1ShortName,
        company2ShortName,
        company1IncorporationType,
        company2IncorporationType,
        company1IncorporationLaw,
        company2IncorporationLaw,
        governingLaw,
        platformName,
        platformUrl,
        currentUserCompany,
        showAllDetails,
        company1SignedInDb,
        company2SignedInDb,
      });

      const blob = new Blob([pdfBytes.buffer as ArrayBuffer], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "NDA-Agreement.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  const AcceptanceBox = ({ accepted, visible }: { accepted: boolean; visible: boolean }) => (
    <div
      style={{
        border: "1px solid #e5e7eb",
        height: 56,
        background: "#fafafa",
        borderRadius: 6,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {!visible ? (
        <div style={{ fontSize: 11, color: "#999", fontStyle: "italic" }}>{HIDDEN_PLACEHOLDER}</div>
      ) : accepted ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#12875a" }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Accepted</span>
        </div>
      ) : (
        <div style={{ fontSize: 12, color: "#999", fontStyle: "italic" }}>Pending acceptance</div>
      )}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Download Button */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={handleDownload}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "7px 14px",
            fontSize: 13,
            fontFamily: "Arial, sans-serif",
            background: "transparent",
            color: "var(--mantine-color-primary-6)",
            border: "1px solid var(--mantine-color-primary-6)",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          <IconDownload size={15} />
          Download PDF
        </button>
      </div>

      {/* Document Preview */}
      <div
        style={{
          background: "white",
          border: "1px solid #D1D5DC",
          borderRadius: 8,
          boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
          padding: "15px 30px",
          maxWidth: 800,
          margin: "0 auto",
          width: "100%",
          fontFamily: "Arial, sans-serif",
          fontSize: 13,
          color: "#1a1a1a",
        }}
      >
        {/* Title */}
        <div style={{ textAlign: "center", marginBottom: 20, paddingBottom: 16, borderBottom: "2px solid #1a1a1a" }}>
          <div
            style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: 18, fontWeight: 700, color: "#1a1a1a" }}
          >
            MUTUAL NON-DISCLOSURE AGREEMENT
          </div>
        </div>

        {/* Preamble */}
        <p style={{ fontSize: 13, lineHeight: 1.75, color: "#333", marginBottom: 16, textAlign: "justify" }}>
          {PARTIES_INTRO}
        </p>

        {/* BETWEEN */}
        <p style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a", marginBottom: 10 }}>BETWEEN</p>
        {/* Party 1 */}
        <p style={{ fontSize: 13, lineHeight: 1.75, color: "#333", marginBottom: 12, textAlign: "justify" }}>
          <strong>{displayCompany1Name}</strong>, a {displayCompany1IncorporationType}, incorporated under the laws of{" "}
          <strong>{displayCompany1IncorporationLaw}</strong>, having its registered office at {displayCompany1Address}{" "}
          (hereinafter referred to as "{displayCompany1ShortName}", which expression shall, unless repugnant to the
          context or meaning thereof, include its successors and permitted assigns);
        </p>

        <p style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a", marginBottom: 10 }}>AND</p>
        {/* Party 2 */}
        <p style={{ fontSize: 13, lineHeight: 1.75, color: "#333", marginBottom: 20, textAlign: "justify" }}>
          <strong>{displayCompany2Name}</strong>, a {displayCompany2IncorporationType}, incorporated under the laws of{" "}
          <strong>{displayCompany2IncorporationLaw}</strong>, having its registered office at {displayCompany2Address}{" "}
          (hereinafter referred to as "{displayCompany2ShortName}", which expression shall, unless repugnant to the
          context or meaning thereof, include its successors and permitted assigns);
        </p>

        {/* WHEREAS */}
        <p style={{ fontSize: 13, lineHeight: 1.75, color: "#333", marginBottom: 20, textAlign: "justify" }}>
          <strong>WHEREAS,</strong> {getWhereas(platformName, platformUrl).replace(/^WHEREAS, /, "")}
        </p>

        <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: "4px 0 20px" }} />

        {/* Clauses */}
        {CLAUSES.map((clause) => (
          <div key={clause.num} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a", marginBottom: 6 }}>
              {clause.num}. {clause.title}
            </div>
            <p style={{ fontSize: 13, lineHeight: 1.75, color: "#333", margin: 0, textAlign: "justify" }}>
              {clause.num === "10" ? (
                <>
                  This Agreement shall be governed by the laws of {governingLaw}. This Agreement is the complete and
                  exclusive agreement regarding disclosure of the Confidential Information.
                </>
              ) : (
                clause.body
              )}
            </p>
          </div>
        ))}

        <hr style={{ border: "none", borderTop: "1px solid #ccc", margin: "24px 0" }} />

        <p style={{ fontSize: 12, fontStyle: "italic", color: "#666", marginBottom: 28, textAlign: "justify" }}>
          IN WITNESS WHEREOF, the Parties have executed this Agreement as of the Effective Date.
        </p>

        {/* Acceptance Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 32,
            marginBottom: 32,
          }}
        >
          {/* Company 1 */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a" }}>{displayCompany1Name || "Company 1"}</div>
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#777",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 3,
                }}
              >
                Registered Address
              </div>
              {!showCompany1Details ? (
                <div style={{ fontSize: 12, color: "#999", lineHeight: 1.5, fontStyle: "italic" }}>
                  {HIDDEN_PLACEHOLDER}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: "#333", lineHeight: 1.5 }}>
                  {company1Address || <span style={{ color: "#999", fontStyle: "italic" }}>No address provided</span>}
                </div>
              )}
            </div>
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#777",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 6,
                }}
              >
                Acceptance
              </div>
              <AcceptanceBox accepted={company1SignedInDb} visible={showCompany1Details} />
            </div>
          </div>

          {/* Company 2 */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a" }}>{displayCompany2Name || "Company 2"}</div>
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#777",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 3,
                }}
              >
                Registered Address
              </div>
              {!showCompany2Details ? (
                <div style={{ fontSize: 12, color: "#999", lineHeight: 1.5, fontStyle: "italic" }}>
                  {HIDDEN_PLACEHOLDER}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: "#333", lineHeight: 1.5 }}>
                  {company2Address || <span style={{ color: "#999", fontStyle: "italic" }}>No address provided</span>}
                </div>
              )}
            </div>
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#777",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 6,
                }}
              >
                Acceptance
              </div>
              <AcceptanceBox accepted={company2SignedInDb} visible={showCompany2Details} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: "1px solid #e5e7eb",
            paddingTop: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 10, color: "#aaa" }}>
            © {new Date().getFullYear()} GTsetu. All rights reserved. This document is confidential.
          </span>
          <span style={{ fontSize: 10, color: "#aaa" }}>Page 1 of 1</span>
        </div>
      </div>
    </div>
  );
}
